import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import LoginForm from "@/components/auth/LoginForm";
import SignupForm from "@/components/auth/SignupForm";
import { Bot, Mail, FileSpreadsheet, Shield, Zap, Users } from "lucide-react";
// import { Agent } from "http";
import { useNavigate } from "react-router-dom";
import { decodeWithBase64 } from "@/Services/encodedecode";

const AgentHub = () => {
  const [currentView, setCurrentView] = useState<'landing' | 'loginagent' | 'dashboard'>('landing');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginModal, setLoginModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem("IsGoogleRedirecting") === "true") {
      sessionStorage.removeItem("IsGoogleRedirecting");
      setCurrentView('loginagent');
      return;
    }
    try {
      const raw = localStorage.getItem("userProfile");
      if (raw) {
        const parsed = JSON.parse(decodeWithBase64(raw));
        if (parsed?.SessionID && parsed?.MACAddress) {
          navigate("/dashboard", { replace: true });
        }
      }
    } catch { }
  }, [navigate]);
  //   const handleLogin = () => {
  //     setIsAuthenticated(true);
  //     setCurrentView('dashboard');
  //   };

  //   const handleLogout = () => {
  //     setIsAuthenticated(false);
  //     setCurrentView('landing');
  //   };

  //   if (currentView === 'dashboard' && isAuthenticated) {
  //     return <Dashboard onLogout={handleLogout} />;
  //   }

  if (currentView === 'loginagent') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <LoginForm setCurrentView={setCurrentView} currentView={currentView} />
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 flex-nowrap">
            <div className="flex items-center space-x-2 min-w-0 flex-1 overflow-hidden">
              <Mail className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
              <span className="font-bold text-gray-900 whitespace-nowrap [font-size:clamp(0.85rem,4.2vw,1.5rem)]">
                Email Tracker
              </span>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              {/* <Button variant="ghost" size="sm" className="px-3" onClick={() => navigate('/')}>Home</Button> */}
              <Button variant="ghost" size="sm" className="px-3" onClick={() => setCurrentView('loginagent')}>
                Login
              </Button>
              {/* <Button onClick={() => setCurrentView('login')}>
                Get Started
              </Button> */}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-6">
            Deploy Your Email Tracker Agent
            <span className="text-blue-600 block mt-2">in Minutes</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Connect your Google Sheets, authorize email access, and let our AI agent track and manage your email workflows automatically.
          </p>
          <Button size="lg" className="text-lg px-8 py-4" onClick={() => setCurrentView('loginagent')}>
            Start Deploying
            <Zap className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Powerful Email Automation Features
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Mail className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle>Email Tracking</CardTitle>
                <CardDescription>
                  Monitor email interactions and track responses automatically
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <FileSpreadsheet className="h-6 w-6 text-green-600" />
                </div>
                <CardTitle>Google Sheets Integration</CardTitle>
                <CardDescription>
                  Seamlessly sync data with your Google Sheets in real-time
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-purple-600" />
                </div>
                <CardTitle>Secure OAuth</CardTitle>
                <CardDescription>
                  Bank-level security with Google OAuth integration
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Deploy in 3 Simple Steps
          </h2>
          <div className="space-y-8">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Connect Your Accounts</h3>
                <p className="text-gray-600">Provide your Google Sheets link and authorize Gmail access through secure OAuth</p>
              </div>
            </div>
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Deploy Your Agent</h3>
                <p className="text-gray-600">Launch your email tracker agent with one click and start monitoring immediately</p>
              </div>
            </div>
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Manage & Monitor</h3>
                <p className="text-gray-600">Control your deployment, view logs, and update settings from your dashboard</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Automate Your Email Workflow?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of users who trust Email Tracker for their email automation needs
          </p>
          <Button
            size="lg"
            variant="secondary"
            className="text-lg px-8 py-4"
            onClick={() => setCurrentView('loginagent')}
          >
            Deploy Your Agent Now
            <Users className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Mail className="h-6 w-6" />
            <span className="text-xl font-bold">Email Tracker</span>
          </div>
          <a
            href="https://cosmitude.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            © Cosmitude Softwares Private Limited. All rights reserved.
          </a>
        </div>
      </footer>
    </div>
  );
};

export default AgentHub;